// speech-recognition.js
//
// Helpers para Web Speech API + getUserMedia. POC: transcribimos en vivo
// SOLO la voz del operador (canal local del browser). El audio remoto
// del gateway TETRA no se transcribe — eso requiere endpoints de audio
// que todavía no existen.
//
// API expuesta:
//   speechSupported    — boolean (compute-once al cargar el módulo)
//   speechHelper       — singleton con start({onResult, onEnd, onError}) y stop()
//   micLevelMeter      — singleton con start(onLevel) y stop()
//   requestMicPermission() → Promise<{ state, error? }>
//   getMicPermissionState() → Promise<'granted'|'denied'|'prompt'|'unknown'>
//
// Compatibilidad:
//   - SpeechRecognition: Chrome/Edge (prefijo webkit en Safari, parcial).
//   - getUserMedia: requiere HTTPS o localhost.
//   - navigator.permissions: opcional, fallback al probe getUserMedia.

const SR = (typeof window !== 'undefined')
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null

export const speechSupported = !!SR

class SpeechHelper {
  constructor() {
    if (typeof SpeechHelper.instance === 'object') return SpeechHelper.instance
    SpeechHelper.instance = this
    this.recognition = null
    this._onResult = null
    this._onEnd = null
    this._onError = null
    this._intentional = false
    this._lastFinalIndex = 0
    if (!speechSupported) return
    this._buildRecognition()
  }

  _buildRecognition() {
    this.recognition = new SR()
    this.recognition.lang = 'es-MX'
    this.recognition.continuous = true
    this.recognition.interimResults = true
    this.recognition.maxAlternatives = 1
    const self = this
    this.recognition.onresult = function(e) {
      var interim = ''
      var freshFinal = ''
      for (var i = e.resultIndex; i < e.results.length; i++) {
        var r = e.results[i]
        if (r.isFinal) freshFinal += r[0].transcript
        else interim += r[0].transcript
      }
      if (self._onResult) self._onResult({ interim: interim, freshFinal: freshFinal })
    }
    this.recognition.onerror = function(e) {
      if (self._onError) self._onError(e.error || 'unknown')
    }
    this.recognition.onend = function() {
      if (self._onEnd) self._onEnd({ intentional: self._intentional })
      self._intentional = false
    }
  }

  start(opts) {
    if (!this.recognition) return false
    this._onResult = (opts && opts.onResult) || null
    this._onEnd    = (opts && opts.onEnd) || null
    this._onError  = (opts && opts.onError) || null
    this._intentional = false
    try {
      this.recognition.start()
      return true
    } catch (e) {
      // Common cuando se llama start() dos veces sin esperar onend:
      // 'InvalidStateError: recognition has already started'. Abortamos
      // el ciclo previo y reintentamos en el siguiente tick.
      try { this.recognition.abort() } catch (_) {}
      var self = this
      setTimeout(function() {
        try { self.recognition.start() } catch (_) {}
      }, 80)
      return true
    }
  }

  stop() {
    if (!this.recognition) return
    this._intentional = true
    try { this.recognition.stop() } catch (_) {}
  }

  abort() {
    if (!this.recognition) return
    this._intentional = true
    try { this.recognition.abort() } catch (_) {}
  }
}

export const speechHelper = new SpeechHelper()

// ───────── Permisos de micrófono ─────────
//
// Estado preferido: navigator.permissions.query({name:'microphone'}).
// Si el browser no soporta esta API (Firefox tradicionalmente, algunos
// Safari), devolvemos 'unknown' y dejamos que requestMicPermission()
// pruebe con un getUserMedia ligero.

export async function getMicPermissionState() {
  try {
    if (navigator.permissions && navigator.permissions.query) {
      const res = await navigator.permissions.query({ name: 'microphone' })
      return res.state || 'unknown'
    }
  } catch (_) { /* fall-through */ }
  return 'unknown'
}

export async function requestMicPermission() {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    return { state: 'denied', error: 'unsupported' }
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    // Liberamos el stream inmediatamente — solo queríamos disparar el
    // prompt nativo y constatar el permiso. El stream "real" se abre
    // por MicLevelMeter cuando hace falta.
    stream.getTracks().forEach(function(t) { t.stop() })
    return { state: 'granted' }
  } catch (err) {
    const name = err && (err.name || err.message) || 'unknown'
    // NotAllowedError → operador rechazó (o policy del browser).
    // NotFoundError → no hay device.
    // SecurityError → no estamos en HTTPS/localhost.
    const state = (name === 'NotAllowedError' || name === 'SecurityError') ? 'denied' : 'denied'
    return { state: state, error: name }
  }
}

// ───────── Mic level meter ─────────
//
// Usa AudioContext + AnalyserNode para calcular el RMS aproximado del
// stream del micrófono. El onLevel callback se llama por rAF con un
// valor 0..1 (escala lineal del RMS normalizado a 0..128 → 0..1).
//
// Uso típico: mientras el operador tiene PTT activo, alimentar una
// barra/halo visual para confirmar que el mic está captando audio. Al
// soltar PTT, llamar .stop() para liberar el stream y cerrar el context.

class MicLevelMeter {
  constructor() {
    if (typeof MicLevelMeter.instance === 'object') return MicLevelMeter.instance
    MicLevelMeter.instance = this
    this.audioContext = null
    this.analyser = null
    this.source = null
    this.stream = null
    this._rafId = null
    this._onLevel = null
    this._dataArray = null
    this._active = false
    this._bandCount = 40
  }

  isActive() { return this._active }

  // start(onLevel, opts) — onLevel se llama con (level, bands) en cada
  // rAF tick mientras el mic esté activo. `bands` es un Array de N floats
  // 0..1 (N = opts.bandCount o 40 por default) que representan la
  // intensidad media de cada banda de frecuencia del FFT — útil para
  // dibujar un waveform tipo equalizer.
  async start(onLevel, opts) {
    if (this._active) return true
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return false
    this._onLevel = onLevel || null
    this._bandCount = (opts && opts.bandCount) || 40
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (err) {
      this._active = false
      return false
    }
    const AC = window.AudioContext || window.webkitAudioContext
    if (!AC) return false
    this.audioContext = new AC()
    this.source = this.audioContext.createMediaStreamSource(this.stream)
    this.analyser = this.audioContext.createAnalyser()
    // fftSize 512 → 256 bins. Da resolución suficiente para 40 bandas
    // (~6 bins/banda) sin saturar el rAF con cálculo innecesario.
    this.analyser.fftSize = 512
    this.analyser.smoothingTimeConstant = 0.65
    this.source.connect(this.analyser)
    this._dataArray = new Uint8Array(this.analyser.frequencyBinCount)
    this._active = true
    const self = this
    const tick = function() {
      if (!self._active || !self.analyser) return
      self.analyser.getByteFrequencyData(self._dataArray)
      // Nivel promedio global (para el meter / barra fina)
      let sum = 0
      for (let i = 0; i < self._dataArray.length; i++) sum += self._dataArray[i]
      const avg = sum / self._dataArray.length
      const norm = Math.min(1, avg / 110)

      // Bands: dividimos los bins en N grupos y promediamos cada uno.
      // La voz humana cubre ~80-8000 Hz, que en 256 bins (~22kHz/2 = 11kHz
      // por nyquist) significa los primeros ~190 bins. Limitamos el rango
      // útil para que las bandas reflejen voz y no agudos vacíos.
      const usefulBins = Math.min(self._dataArray.length, 180)
      const binsPerBand = Math.max(1, Math.floor(usefulBins / self._bandCount))
      const bands = new Array(self._bandCount)
      for (let b = 0; b < self._bandCount; b++) {
        let bandSum = 0
        const start = b * binsPerBand
        const end = Math.min(start + binsPerBand, usefulBins)
        for (let i = start; i < end; i++) bandSum += self._dataArray[i]
        const bandAvg = bandSum / Math.max(1, (end - start))
        // Normalización empírica: bandas de voz típicas suelen estar
        // 20-100; dividimos por 140 y clampamos. Aplicamos también un
        // floor de 0.05 para que las barras nunca colapsen a 0 visible.
        bands[b] = Math.max(0.05, Math.min(1, bandAvg / 140))
      }

      if (self._onLevel) self._onLevel(norm, bands)
      self._rafId = requestAnimationFrame(tick)
    }
    tick()
    return true
  }

  stop() {
    this._active = false
    if (this._rafId) { cancelAnimationFrame(this._rafId); this._rafId = null }
    if (this.source) {
      try { this.source.disconnect() } catch (_) {}
      this.source = null
    }
    if (this.stream) {
      this.stream.getTracks().forEach(function(t) { try { t.stop() } catch (_) {} })
      this.stream = null
    }
    if (this.audioContext) {
      try { this.audioContext.close() } catch (_) {}
      this.audioContext = null
    }
    this.analyser = null
    this._dataArray = null
    this._onLevel = null
  }
}

export const micLevelMeter = new MicLevelMeter()

export default {
  speechSupported,
  speechHelper,
  micLevelMeter,
  requestMicPermission,
  getMicPermissionState,
}
